import { AuthCallbackPageComponent, LoginPageComponent } from 'pages';
import { Navigate, Route, Routes } from 'react-router-dom';
import { ROUTES } from 'router/const';
import { PrivateRoute } from './guards/PrivateRoute';
import { PublicRoute } from './guards/PublicRoute';
import { AppLayout } from 'components/layouts/AppLayout';
import { GuestRoute } from './guards/GuestRoute';
import { useAuth } from 'hooks/Auth/useAuth';
import { EmbedRoute } from './guards/EmbedRoute';
import { EmbedLayout } from 'components/layouts/EmbedLayout';

export const AppRoutes = () => {
  const { isLoggedIn } = useAuth();

  return (
    <Routes>
      <Route element={<EmbedLayout />}>
        {/* ================== Embed Route ================== */}
        {/* 로그인 여부와 관계없는 Embedded 전용 페이지 */}
        <Route path={ROUTES.EMBED.PATH} element={<EmbedRoute />}>
          <Route path={ROUTES.EMBED.REPORT.PATH} element={<ROUTES.EMBED.REPORT.COMPONENT />} />
        </Route>
      </Route>

      <Route element={<AppLayout />}>
        {/* ================== Public Route ================== */}
        {/* 로그인 여부와 관계없이 접속 가능한 페이지 */}
        <Route element={<PublicRoute />}>
          {/* Auth Callback Page */}
          <Route path={ROUTES.AUTH_CALLBACK.PATH} element={<AuthCallbackPageComponent />} />
        </Route>
        {/* ================== Public Route ================== */}

        {/* ================== Guest Route ================== */}
        {/* 미로그인 시에만 접속 가능한 페이지 */}
        <Route element={<GuestRoute isAuthenticated={isLoggedIn} />}>
          {/* Login Page */}
          <Route path={ROUTES.LOGIN.PATH} element={<LoginPageComponent />} />
          <Route path={ROUTES.EXTERNAL.AUTH.PATH} element={<ROUTES.EXTERNAL.AUTH.COMPONENT />} />
        </Route>
        {/* ================== Guest Route ================== */}

        {/* ================== Private Route ================== */}
        {/* 로그인 시에만 접속 가능한 페이지 */}
        <Route element={<PrivateRoute isAuthenticated={isLoggedIn} />}>
          <Route path={ROUTES.ROOT.PATH} element={<ROUTES.ROOT.COMPONENT />} />

          {/* Library Page */}
          <Route path={ROUTES.LIBRARY.PATH}>
            <Route index element={<Navigate to={ROUTES.LIBRARY.DATASETS.PATH} replace />} />
            <Route
              path={ROUTES.LIBRARY.DATASETS.PATH}
              element={<ROUTES.LIBRARY.DATASETS.COMPONENT />}
            />
            <Route
              path={ROUTES.LIBRARY.DATASOURCES.PATH}
              element={<ROUTES.LIBRARY.DATASOURCES.COMPONENT />}
            />
            <Route path={ROUTES.LIBRARY.LOGS.PATH} element={<ROUTES.LIBRARY.LOGS.COMPONENT />} />
          </Route>

          {/* Mart Page */}
          <Route path={ROUTES.MART.PATH}>
            <Route index element={<Navigate to={ROUTES.MART.ANSWERSET.PATH} replace />} />
            <Route
              path={ROUTES.MART.ANSWERSET.PATH}
              element={<ROUTES.MART.ANSWERSET.COMPONENT />}
            />
            <Route path={ROUTES.MART.LOGS.PATH} element={<ROUTES.MART.LOGS.COMPONENT />} />
          </Route>

          {/* Manual Page */}
          <Route path={ROUTES.MANUAL.PATH} element={<ROUTES.MANUAL.COMPONENT />} />

          {/* Project Page */}
          <Route path={ROUTES.PROJECT.PATH}>
            <Route index element={<Navigate to={ROUTES.PROJECT.LIST.PATH} replace />} />
            <Route path={ROUTES.PROJECT.LIST.PATH} element={<ROUTES.PROJECT.LIST.COMPONENT />} />
            <Route path={ROUTES.PROJECT.DATA.PATH} element={<ROUTES.PROJECT.DATA.COMPONENT />} />
            <Route
              path={ROUTES.PROJECT.JUPYTER.PATH}
              element={<ROUTES.PROJECT.JUPYTER.COMPONENT />}
            />
          </Route>

          {/* Flow Page */}
          <Route path={ROUTES.FLOW.PATH}>
            <Route index element={<Navigate to={ROUTES.FLOW.FLOWS.PATH} replace />} />
            <Route path={ROUTES.FLOW.FLOWS.PATH} element={<ROUTES.FLOW.FLOWS.COMPONENT />} />
            <Route path={ROUTES.FLOW.GROUPS.PATH} element={<ROUTES.FLOW.GROUPS.COMPONENT />} />
            <Route path={ROUTES.FLOW.LOGS.PATH} element={<ROUTES.FLOW.LOGS.COMPONENT />} />
          </Route>

          {/* Report Page */}
          <Route path={ROUTES.REPORT.PATH}>
            <Route index element={<Navigate to={ROUTES.REPORT.DRAFT_LIST.PATH} replace />} />
            <Route
              path={ROUTES.REPORT.DRAFT_LIST.PATH}
              element={<ROUTES.REPORT.DRAFT_LIST.COMPONENT />}
            />
            <Route
              path={ROUTES.REPORT.DRAFT_DATA.PATH}
              element={<ROUTES.REPORT.DRAFT_DATA.COMPONENT />}
            />
            <Route
              path={ROUTES.REPORT.PUBLISH_LIST.PATH}
              element={<ROUTES.REPORT.PUBLISH_LIST.COMPONENT />}
            />
            <Route
              path={ROUTES.REPORT.PUBLISH_DATA.PATH}
              element={<ROUTES.REPORT.PUBLISH_DATA.COMPONENT />}
            />
            <Route
              path={ROUTES.REPORT.CONNECTION_LIST.PATH}
              element={<ROUTES.REPORT.CONNECTION_LIST.COMPONENT />}
            />
            <Route
              path={ROUTES.REPORT.CONNECTION_DATA.PATH}
              element={<ROUTES.REPORT.CONNECTION_DATA.COMPONENT />}
            />
          </Route>

          {/* Admin Page */}
          <Route path={ROUTES.ADMIN.PATH}>
            <Route index element={<Navigate to={ROUTES.ADMIN.USERS.PATH} replace />} />
            <Route path={ROUTES.ADMIN.USERS.PATH} element={<ROUTES.ADMIN.USERS.COMPONENT />} />
            <Route path={ROUTES.ADMIN.GROUPS.PATH} element={<ROUTES.ADMIN.GROUPS.COMPONENT />} />
            <Route path={ROUTES.ADMIN.ROLES.PATH} element={<ROUTES.ADMIN.ROLES.COMPONENT />} />
            <Route
              path={ROUTES.ADMIN.CONNECTORS.PATH}
              element={<ROUTES.ADMIN.CONNECTORS.COMPONENT />}
            />
            <Route path={ROUTES.ADMIN.DOMAINS.PATH} element={<ROUTES.ADMIN.DOMAINS.COMPONENT />} />
            <Route
              path={ROUTES.ADMIN.USER_LOGS.PATH}
              element={<ROUTES.ADMIN.USER_LOGS.COMPONENT />}
            />
            <Route
              path={ROUTES.ADMIN.MONITORING.PATH}
              element={<ROUTES.ADMIN.MONITORING.COMPONENT />}
            />
          </Route>

          {/* External Page */}
          <Route path={ROUTES.EXTERNAL.PATH}>
            <Route index element={<Navigate to={ROUTES.EXTERNAL.JUPYTER.PATH} replace />} />
            <Route
              path={ROUTES.EXTERNAL.JUPYTER.PATH}
              element={<ROUTES.EXTERNAL.JUPYTER.COMPONENT />}
            />
          </Route>

          {/* Not Found */}
          <Route path={ROUTES.NOT_FOUND.PATH} element={<ROUTES.NOT_FOUND.COMPONENT />} />
        </Route>
        {/* ================== Private Route ================== */}
      </Route>
    </Routes>
  );
};
